<template>
  <div class="grid grid-cols-5">
    <bubble class="col-span-3 col-start-3 justify-end"
            :bubbleClasses="bubbleClasses"
            :textClasses="textClasses"
            :buttonClasses="buttonClasses"
            :quickReplyClasses="quickReplyClasses"
            :content="action.content"
            :showTemplateIcon="state.type === STATE_TYPES.TEMPLATE_REF"
            @select-text="selectText"
            @select-image="selectImage"
            @select-button="selectButton"
            @select-quick-reply="selectQuickReply">
    </bubble>
  </div>
</template>

<script>
import { SECTION_FOCUSES } from '@/store/constants'
import templateSelectorModule from '@/store/modules/template-selector.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import conversationModule from '@/store/modules/conversation.module'
import previewModule from '@/store/modules/preview.module'
import { STATE_TYPES } from '@/utils/bot-json.constants'
import Bubble from './Bubble.vue'

export default {
  components: { Bubble },

  props: {
    actionId: { type: String, required: true },
    stateId: { type: String, required: true },
    isTemplate: { type: Boolean, default: false },
    inNestedCondition: { type: Boolean, default: false }
  },

  data() {
    return {
      SECTION_FOCUSES,
      STATE_TYPES
    }
  },

  computed: {
    action() {
      return conversationModule.getAction(this.stateId, this.actionId)
    },

    bubbleClasses() {
      const defaultClasses = 'hover:border-focus hover:border-1 cursor-pointer'
      if (this.isErrorBubbleFocused && this.actionId === this.error.actionId) {
        return `${defaultClasses} border-focus-error`
      }

      return this.isSelected ? `${defaultClasses} bubble-shadow border-focus` : defaultClasses
    },

    bubbleSelected: chatBubbleModule.bubbleSelected,
    error: previewModule.error,

    isErrorBubbleFocused() {
      return chatBubbleModule.isErrorBubbleFocused()
    },

    errorPath() {
      if (!this.bubbleSelected.error) return ''

      return this.bubbleSelected.error.path
    },

    focus: chatBubbleModule.sectionFocus,

    isSelected() {
      return this.bubbleSelected.actionId === this.actionId && this.bubbleSelected.stateId === this.stateId
    },

    state() {
      return conversationModule.getRootJson().states[this.stateId]
    },

    textClasses() {
      let textClasses = 'whitespace-pre-wrap'
      const addClass = newClass => {textClasses = `${textClasses} ${newClass}`}

      if (this.isErrorTextBubbleFocused) {
        addClass('border-focus-error')
        return textClasses
      }

      addClass('hover:border-focus hover:border-1')
      if (this.textFocused) addClass('border-focus')
      if (!this.textFocused) addClass('border-primary')

      return textClasses
    },

    isErrorTextBubbleFocused() {
      return this.isSelected && this.bubbleSelected.error && this.errorPath === 'text'
    },

    noneFocused() {
      return this.bubbleSelected.sectionFocus === SECTION_FOCUSES.NONE
    },

    textFocused() {
      return this.bubbleSelected.sectionFocus === SECTION_FOCUSES.TEXT && this.isSelected
    }
  },

  methods: {
    selectBubble(sectionFocus) {
      chatBubbleModule.selectBotBubble(this.actionId, this.stateId, sectionFocus, this.state.type === STATE_TYPES.TEMPLATE_REF, this.inNestedCondition)
      templateSelectorModule.hideSelector()
    },

    selectText() {
      this.resetSectionFocus()
      this.selectBubble(SECTION_FOCUSES.TEXT)
    },

    selectImage() {
      this.resetSectionFocus()
      this.selectBubble(SECTION_FOCUSES.IMAGE)
    },

    selectButton(index) {
      this.resetSectionFocus()
      const newFocus = SECTION_FOCUSES.BUTTON + index
      this._toggleSection(newFocus)
    },

    selectQuickReply(index) {
      this.resetSectionFocus()
      const newFocus = SECTION_FOCUSES.QUICK_REPLY + index
      this._toggleSection(newFocus)
    },

    _toggleSection(newFocus) {
      if (this.focus === newFocus) {
        this.selectBubble(SECTION_FOCUSES.NONE)
        return
      }

      this.selectBubble(newFocus)
    },

    buttonClasses(index) {
      const defaultClasses = 'hover:border-focus hover:border-1'
      const focus = this.bubbleSelected.sectionFocus
      if (this.isSelected && focus === SECTION_FOCUSES.BUTTON + index) {
        if (this.isErrorBubbleFocused || this.errorPath.startsWith('buttons')) {
          return `${defaultClasses} border-focus-error`
        }
        return `${defaultClasses} border-focus bg-primary-focus`
      }

      return `${defaultClasses} border-white`
    },

    quickReplyClasses(index) {
      const defaultClasses = 'hover:cursor-pointer hover:border-focus hover:border-1'
      const focus = this.bubbleSelected.sectionFocus
      if (this.isSelected && focus === SECTION_FOCUSES.QUICK_REPLY + index) {
        if (this.isErrorBubbleFocused || this.errorPath.startsWith('quick_replies')) {
          return `${defaultClasses} border-focus-error text-focus-error`
        }
        return `${defaultClasses} border-focus text-focus`
      }

      return `${defaultClasses} border-primary text-primary`
    },

    resetSectionFocus() {
      chatBubbleModule.focusSection(SECTION_FOCUSES.NONE)
    }
  }
}
</script>
<style scoped>
::v-deep a {
  @apply text-white underline;
}
</style>
